import React, { useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { fmtINR } from "@/lib/format";
import { CheckCircle2, ShieldCheck, AlertTriangle, Sparkles, ArrowRight, Loader2, TrendingUp } from "lucide-react";

/**
 * Unified OnboardingWizard — the Vendor wizard extended for Customers too.
 *
 * Props:
 *   partyType: "vendor" | "customer"
 *   open, onOpenChange, onCreated
 *
 * For customers, step-2 also shows Payment History (if the customer already
 * exists under this org) and a **recommended credit limit in ₹** with a
 * reasoning chain.
 */
const EMPTY = {
  name: "", gstin: "", pan: "", email: "", phone: "", state: "", address: "",
  years_in_business: "", annual_turnover_inr: "", payment_terms_days: 30,
  default_tds_section: "",
};
const BAND_TONE = {
  Excellent: "text-emerald-600 bg-emerald-500/10 border-emerald-500/25",
  Good:      "text-blue-600    bg-blue-500/10    border-blue-500/25",
  Fair:      "text-amber-600   bg-amber-500/10   border-amber-500/25",
  Poor:      "text-red-600     bg-red-500/10     border-red-500/25",
};

export default function OnboardingWizard({ open, onOpenChange, onCreated, partyType = "vendor" }) {
  const noun = partyType === "customer" ? "customer" : "vendor";
  const Noun = noun[0].toUpperCase() + noun.slice(1);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setStep(1); setForm(EMPTY); setPreview(null); };
  const closeAndReset = (v) => { onOpenChange(v); if (!v) reset(); };

  const runPreview = async () => {
    if (!form.name) return toast.error(`${Noun} name is required`);
    setBusy(true);
    try {
      const r = await api.post(`/${noun}s/preview`, form);
      setPreview(r.data);
      setStep(2);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    setBusy(false);
  };
  const commit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/${noun}s/onboard`, form);
      const created = r.data[noun];
      toast.success(`${Noun} "${created.name}" onboarded · risk ${created.risk_score}`);
      onCreated?.(created);
      closeAndReset(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Commit failed"); }
    setBusy(false);
  };

  const risk = preview?.risk;
  const gstn = preview?.gstin_lookup;
  const tds  = preview?.tds;
  const hist = preview?.history;
  const cLimit = preview?.credit_limit_inr_recommended;

  return (
    <Dialog open={open} onOpenChange={closeAndReset}>
      <DialogContent className="max-w-2xl" data-testid={`${noun}-wizard`}>
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-accent" /> Onboard a new {noun}
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-2 mb-4">
          {["Details", "Preview & score"].map((label, i) => {
            const idx = i + 1; const on = step === idx; const done = step > idx;
            return (
              <React.Fragment key={label}>
                <div className={`flex items-center gap-1.5 text-[11px] ${on ? "text-accent font-semibold" : done ? "text-emerald-600" : "text-muted-foreground"}`}
                     data-testid={`ow-step-${idx}`}>
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-mono border ${on ? "border-accent bg-accent text-white" : done ? "border-emerald-500 bg-emerald-500 text-white" : "border-border"}`}>
                    {done ? "✓" : idx}
                  </div>
                  {label}
                </div>
                {idx < 2 && <div className="flex-1 h-px bg-border" />}
              </React.Fragment>
            );
          })}
        </div>

        {step === 1 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label>{Noun} name *</Label>
              <Input required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                     className="mt-1 h-10 rounded-sm" data-testid="ow-name" />
            </div>
            <div>
              <Label>GSTIN <span className="text-[10px] text-muted-foreground">(auto-verifies)</span></Label>
              <Input value={form.gstin} maxLength={15}
                     onChange={e => setForm({...form, gstin: e.target.value.toUpperCase()})}
                     className="mt-1 h-10 rounded-sm font-mono uppercase" data-testid="ow-gstin"
                     autoCapitalize="characters" />
            </div>
            <div>
              <Label>PAN <span className="text-[10px] text-muted-foreground">(inferred from GSTIN)</span></Label>
              <Input value={form.pan} maxLength={10}
                     onChange={e => setForm({...form, pan: e.target.value.toUpperCase()})}
                     className="mt-1 h-10 rounded-sm font-mono uppercase" data-testid="ow-pan" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                     className="mt-1 h-10 rounded-sm" data-testid="ow-email" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                     className="mt-1 h-10 rounded-sm" data-testid="ow-phone" />
            </div>
            <div>
              <Label>Years in business</Label>
              <Input type="number" value={form.years_in_business}
                     onChange={e => setForm({...form, years_in_business: e.target.value})}
                     className="mt-1 h-10 rounded-sm text-right tabular-nums" data-testid="ow-years" />
            </div>
            <div>
              <Label>Annual turnover (₹)</Label>
              <Input type="number" value={form.annual_turnover_inr}
                     onChange={e => setForm({...form, annual_turnover_inr: e.target.value})}
                     className="mt-1 h-10 rounded-sm text-right tabular-nums" data-testid="ow-turnover" />
            </div>
            <div>
              <Label>Payment terms (days)</Label>
              <Input type="number" value={form.payment_terms_days}
                     onChange={e => setForm({...form, payment_terms_days: Number(e.target.value)})}
                     className="mt-1 h-10 rounded-sm text-right tabular-nums" data-testid="ow-terms" />
            </div>
            <div className="md:col-span-2">
              <Label>Address</Label>
              <Input value={form.address} onChange={e => setForm({...form, address: e.target.value})}
                     className="mt-1 h-10 rounded-sm" data-testid="ow-address" />
            </div>
          </div>
        )}

        {step === 2 && preview && (
          <div className="space-y-3">
            <div className={`p-3 rounded-sm border ${preview.gstin_valid ? "border-emerald-500/25 bg-emerald-500/5" : "border-amber-500/25 bg-amber-500/5"}`}
                 data-testid="ow-gstin-preview">
              <div className="flex items-center gap-2 font-medium">
                {preview.gstin_valid
                  ? <><ShieldCheck className="w-4 h-4 text-emerald-500" /> GSTIN verified {gstn?.source === "gst_portal" && "(gst.gov.in)"}</>
                  : <><AlertTriangle className="w-4 h-4 text-amber-500" /> GSTIN not verified</>}
              </div>
              {preview.gstin_valid && (
                <div className="mt-1 text-[11px] space-y-0.5">
                  <div><b>Legal name:</b> {gstn?.legal_name || "—"}</div>
                  <div><b>PAN:</b> {preview.pan_inferred} · <b>State:</b> {gstn?.state}</div>
                </div>
              )}
            </div>

            {partyType === "vendor" && (
              <div className="p-3 rounded-sm border bg-muted/30" data-testid="ow-tds-preview">
                <div className="text-[10px] font-mono uppercase text-muted-foreground">// Suggested TDS</div>
                <div className="mt-1 text-sm">
                  <span className="font-mono font-semibold text-accent">Sec {tds.section}</span> @ <span className="font-mono">{tds.rate}%</span>
                </div>
                <div className="text-[10px] text-muted-foreground italic">{tds.reason}</div>
              </div>
            )}

            {risk && (
              <div className={`p-4 rounded-sm border ${BAND_TONE[risk.band] || ""}`} data-testid="ow-risk-preview">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[10px] font-mono uppercase opacity-80">// {Noun} risk score</div>
                    <div className="text-3xl font-bold mt-1 font-mono">{risk.score}<span className="text-sm font-normal opacity-60"> / 900</span></div>
                    <div className="text-sm font-semibold mt-0.5">{risk.band}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] font-mono uppercase opacity-70">Recommended credit</div>
                    <div className="font-mono text-lg font-semibold">{risk.recommended_credit_days} days</div>
                    {partyType === "customer" && cLimit != null && (
                      <div className="mt-1 text-[10px] font-mono uppercase opacity-70">Credit limit</div>
                    )}
                    {partyType === "customer" && cLimit != null && (
                      <div className="font-mono text-lg font-semibold text-emerald-600" data-testid="ow-credit-limit">{fmtINR(cLimit)}</div>
                    )}
                  </div>
                </div>
                <ul className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] font-mono opacity-85">
                  {risk.reasons.map((r_, i) => <li key={i}>· {r_}</li>)}
                </ul>
                {partyType === "customer" && preview.credit_limit_reasoning && (
                  <div className="mt-3 pt-3 border-t border-current/10">
                    <div className="text-[10px] font-mono uppercase opacity-70">Credit-limit math</div>
                    <ul className="mt-1 text-[11px] font-mono opacity-85 space-y-0.5">
                      {preview.credit_limit_reasoning.map((x, i) => <li key={i}>· {x}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {partyType === "customer" && hist && hist.invoices > 0 && (
              <div className="p-3 rounded-sm border bg-blue-500/5 border-blue-500/25" data-testid="ow-history-preview">
                <div className="flex items-center gap-1.5 text-blue-700 font-medium">
                  <TrendingUp className="w-4 h-4" /> Existing customer · payment history
                </div>
                <div className="mt-1 grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px]">
                  <div><div className="text-muted-foreground">Invoices</div><div className="font-mono font-semibold">{hist.invoices}</div></div>
                  <div><div className="text-muted-foreground">Total billed</div><div className="font-mono font-semibold">{fmtINR(hist.total_billed_inr)}</div></div>
                  <div><div className="text-muted-foreground">On-time %</div><div className="font-mono font-semibold">{hist.on_time_pct}%</div></div>
                  <div><div className="text-muted-foreground">Overdue</div><div className="font-mono font-semibold">{hist.overdue_count}</div></div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between mt-4">
          <div className="text-[11px] text-muted-foreground">Step {step} of 2</div>
          <div className="flex items-center gap-2">
            {step === 2 && (
              <Button variant="outline" size="sm" onClick={() => setStep(1)} className="rounded-sm" data-testid="ow-back">Back</Button>
            )}
            {step === 1 && (
              <Button onClick={runPreview} disabled={busy || !form.name} size="sm"
                        className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="ow-preview-btn">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                Preview & score <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            )}
            {step === 2 && (
              <Button onClick={commit} disabled={busy} size="sm"
                        className="rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white gap-1.5" data-testid="ow-commit-btn">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                Create {noun}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
